import React, { useRef, useCallback, useEffect } from 'react';
import { AspectRatio, Box, Center, Group, Stack, Text } from '@mantine/core';
import { IconCamera, IconPhotoUp, IconTrash } from '@tabler/icons-react';
import { isMobile } from 'is-mobile';
import { ProductPreviewFrame } from '@/components/ProductPreviewFrame';
import { EditorChipButton } from '@/components/EditorChipButton';
import {
  PRODUCT_CARD_PREVIEW_BG,
  PRODUCT_CARD_PREVIEW_IMAGE_HEIGHT,
} from '@/constants/productPreview';
import {
  capturePreviewFromViewer,
  normalizeProductPreviewToObjectUrl,
  type ModelViewerPreviewCapture,
} from '@/utils/productPreviewImage';
import * as modelViewer from '@google/model-viewer';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store/Store';
import { notifications } from '@mantine/notifications';

const MIN_VIEWER_DRAW_PX = 64;

/** Waits until model-viewer has non-zero layout (avoids WebGL framebuffer errors during preview capture). */
function whenDrawableModelViewerSize(
  el: modelViewer.ModelViewerElement,
  timeoutMs = 8000
): Promise<boolean> {
  const ready = () => el.clientWidth >= MIN_VIEWER_DRAW_PX && el.clientHeight >= MIN_VIEWER_DRAW_PX;
  if (ready()) return Promise.resolve(true);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      ro.disconnect();
      window.clearTimeout(timer);
      resolve(ok);
    };
    const ro = new ResizeObserver(() => {
      if (ready()) finish(true);
    });
    ro.observe(el);
    const timer = window.setTimeout(() => finish(false), timeoutMs);
  });
}

type PreviewCaptureHandlers = Pick<
  PreviewImageManagerProps,
  | 'onPreviewUriChange'
  | 'onInitialCameraOrbitChange'
  | 'onInitialCameraTargetChange'
  | 'onInitialCameraFieldOfViewChange'
>;

function viewerSrcMatches(el: modelViewer.ModelViewerElement, url: string): boolean {
  if (!url) return false;
  const attr = el.getAttribute('src') || '';
  const src = el.src || '';
  return attr === url || src === url;
}

function commitPreviewCapture(
  capture: ModelViewerPreviewCapture,
  previewUriRef: React.MutableRefObject<string>,
  handlers: PreviewCaptureHandlers,
  revokePrevious: boolean
) {
  const prev = previewUriRef.current;
  if (revokePrevious && prev && prev.startsWith('blob:') && prev !== capture.url) {
    URL.revokeObjectURL(prev);
  }
  previewUriRef.current = capture.url;
  handlers.onPreviewUriChange(capture.url);
  handlers.onInitialCameraOrbitChange(capture.cameraOrbit);
  handlers.onInitialCameraTargetChange(capture.cameraTarget);
  handlers.onInitialCameraFieldOfViewChange(capture.cameraFieldOfView);
}

interface PreviewImageManagerProps {
  modelPreviewUri: string;
  modelViewerRef: React.RefObject<modelViewer.ModelViewerElement>;
  onPreviewUriChange: (uri: string) => void;
  onInitialCameraOrbitChange: (orbit: string) => void;
  onInitialCameraTargetChange: (target: string) => void;
  onInitialCameraFieldOfViewChange: (fov: string) => void;
  modelUrl: string;
  /**
   * When true (e.g. new product create flow), captures preview from the viewer once after the model loads
   * if the user has not set a preview yet — same as clicking "Current model view".
   */
  autoCaptureFromViewerOnLoad?: boolean;
  /**
   * Identifies which product this capture belongs to (batch create).
   * Parent should ignore commits if this no longer matches the active product.
   */
  captureOwnerId?: string;
}

export function PreviewImageManager({
  modelPreviewUri,
  modelViewerRef,
  onPreviewUriChange,
  onInitialCameraOrbitChange,
  onInitialCameraTargetChange,
  onInitialCameraFieldOfViewChange,
  modelUrl,
  autoCaptureFromViewerOnLoad = false,
  captureOwnerId,
}: PreviewImageManagerProps) {
  const { t } = useTranslation();
  const color: string = useStore((state) => state.userProfile.qrCodeColor);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** Keep in sync during render so product switches do not use the previous draft's URI. */
  const previewUriRef = useRef(modelPreviewUri);
  previewUriRef.current = modelPreviewUri;
  /** Prevents duplicate auto-capture for the same model URL. */
  const autoCapturedForUrlRef = useRef<string>('');
  /** Bumped when the active model changes so in-flight captures are discarded. */
  const captureGenRef = useRef(0);

  const captureOwnerIdRef = useRef(captureOwnerId);
  captureOwnerIdRef.current = captureOwnerId;
  const modelUrlRef = useRef(modelUrl);
  modelUrlRef.current = modelUrl;

  const handlersRef = useRef<PreviewCaptureHandlers>({
    onPreviewUriChange,
    onInitialCameraOrbitChange,
    onInitialCameraTargetChange,
    onInitialCameraFieldOfViewChange,
  });
  handlersRef.current = {
    onPreviewUriChange,
    onInitialCameraOrbitChange,
    onInitialCameraTargetChange,
    onInitialCameraFieldOfViewChange,
  };

  useEffect(() => {
    captureGenRef.current += 1;
    if (!modelUrl) autoCapturedForUrlRef.current = '';
  }, [modelUrl, captureOwnerId]);

  const captureFromViewer = useCallback(async () => {
    const mv = modelViewerRef.current;
    const url = modelUrlRef.current;
    if (!mv || !url) return;
    try {
      const sized = await whenDrawableModelViewerSize(mv);
      if (!sized) {
        notifications.show({
          title: t('editProduct.editError'),
          message: t('editProduct.previewCaptureError'),
          color: 'yellow',
        });
        return;
      }
      if (!viewerSrcMatches(mv, url)) return;
      const capture = await capturePreviewFromViewer(mv);
      if (!viewerSrcMatches(mv, modelUrlRef.current)) return;
      commitPreviewCapture(capture, previewUriRef, handlersRef.current, true);
    } catch (error) {
      console.error('Preview capture from model-viewer failed:', error);
      notifications.show({
        title: t('editProduct.editError'),
        message: t('editProduct.previewCaptureError'),
        color: 'red',
      });
    }
  }, [modelViewerRef, t]);

  // Auto-capture only on the viewer's `load` event for the current src.
  // Never snapshot based on leftover `loaded` — after src changes the flag can
  // still be true while WebGL still shows the previous GLB.
  useEffect(() => {
    if (!autoCaptureFromViewerOnLoad) return;

    let cancelled = false;
    let attachedEl: modelViewer.ModelViewerElement | null = null;

    const tryCapture = async (el: modelViewer.ModelViewerElement) => {
      const gen = captureGenRef.current;
      const url = modelUrlRef.current;
      const owner = captureOwnerIdRef.current;
      if (!url || cancelled) return;
      if (!viewerSrcMatches(el, url)) return;
      if (previewUriRef.current) return;
      if (autoCapturedForUrlRef.current === url) return;

      try {
        await new Promise<void>((r) =>
          requestAnimationFrame(() => requestAnimationFrame(() => r()))
        );
        if (cancelled || gen !== captureGenRef.current || previewUriRef.current) return;
        if (modelUrlRef.current !== url || captureOwnerIdRef.current !== owner) return;
        if (!viewerSrcMatches(el, url)) return;

        const sized = await whenDrawableModelViewerSize(el);
        if (!sized || cancelled || gen !== captureGenRef.current || previewUriRef.current) return;
        if (modelUrlRef.current !== url || captureOwnerIdRef.current !== owner) return;
        if (!viewerSrcMatches(el, url)) return;

        const capture = await capturePreviewFromViewer(el);
        if (cancelled || gen !== captureGenRef.current || previewUriRef.current) return;
        if (modelUrlRef.current !== url || captureOwnerIdRef.current !== owner) return;
        if (!viewerSrcMatches(el, url)) return;

        commitPreviewCapture(capture, previewUriRef, handlersRef.current, false);
        autoCapturedForUrlRef.current = url;
      } catch (error) {
        console.error('Auto preview capture failed:', error);
      }
    };

    const onLoad = () => {
      if (cancelled) return;
      const el = attachedEl ?? modelViewerRef.current;
      if (el) void tryCapture(el);
    };

    const attach = (): boolean => {
      const el = modelViewerRef.current;
      if (!el || el === attachedEl) return Boolean(el);
      if (attachedEl) attachedEl.removeEventListener('load', onLoad);
      attachedEl = el;
      el.addEventListener('load', onLoad);
      return true;
    };

    const waitForElement = async () => {
      for (let i = 0; i < 80 && !cancelled; i += 1) {
        if (attach()) return;
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
      }
    };

    void waitForElement();
    const poll = window.setInterval(() => {
      if (!cancelled) attach();
    }, 500);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      if (attachedEl) attachedEl.removeEventListener('load', onLoad);
    };
  }, [autoCaptureFromViewerOnLoad, modelViewerRef]);

  const handlePreviewImageUpload = (file: File | null) => {
    if (!file) {
      onPreviewUriChange('');
      return;
    }

    if (!file.type.startsWith('image/')) {
      notifications.show({
        title: t('editProduct.editError'),
        message: t('editProduct.onlyImages'),
        color: 'red',
      });
      return;
    }

    void (async () => {
      try {
        const prev = previewUriRef.current;
        if (prev && prev.startsWith('blob:')) {
          URL.revokeObjectURL(prev);
        }
        const url = await normalizeProductPreviewToObjectUrl(file);
        previewUriRef.current = url;
        onPreviewUriChange(url);
        onInitialCameraOrbitChange('');
        onInitialCameraTargetChange('');
        onInitialCameraFieldOfViewChange('');
      } catch (error) {
        console.error('Preview upload normalize failed:', error);
        notifications.show({
          title: t('editProduct.editError'),
          message: t('editProduct.previewCaptureError'),
          color: 'red',
        });
      }
    })();
  };

  const removePreview = () => {
    const prev = previewUriRef.current;
    if (prev && prev.startsWith('blob:')) {
      URL.revokeObjectURL(prev);
    }
    previewUriRef.current = '';
    onPreviewUriChange('');
    onInitialCameraOrbitChange('');
    onInitialCameraTargetChange('');
    onInitialCameraFieldOfViewChange('');
  };

  const previewHeight = isMobile()
    ? PRODUCT_CARD_PREVIEW_IMAGE_HEIGHT.mobile
    : PRODUCT_CARD_PREVIEW_IMAGE_HEIGHT.desktop;

  return (
    <Box mb="md">
      <Text size="sm" fw={500} mb="xs">
        {t('editProduct.previewImage')}
      </Text>

      <Group align="flex-start" gap="md">
        <Box style={{ flexShrink: 0 }}>
          {modelPreviewUri ? (
            <Stack gap="xs" align="flex-start">
              <ProductPreviewFrame src={modelPreviewUri} alt={t('editProduct.previewImage')} />
              <EditorChipButton
                variant="danger"
                leftIcon={<IconTrash size={15} stroke={1.75} />}
                onClick={removePreview}
              >
                {t('editProduct.removePreview')}
              </EditorChipButton>
            </Stack>
          ) : (
            <AspectRatio ratio={1} style={{ height: previewHeight, width: previewHeight }}>
              <Center
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: PRODUCT_CARD_PREVIEW_BG,
                  borderRadius: 'var(--mantine-radius-md)',
                  border: '1px dashed var(--mantine-color-gray-4)',
                  padding: '8px',
                }}
              >
                <Text size="xs" c="dimmed" ta="center">
                  {t('editProduct.noPreviewSelected')}
                </Text>
              </Center>
            </AspectRatio>
          )}
        </Box>

        <Stack gap="xs" align="flex-end" style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
          <Text size="xs" c="dimmed" ta="left" style={{ alignSelf: 'stretch' }}>
            {autoCaptureFromViewerOnLoad
              ? t('editProduct.previewActionsHintAuto')
              : t('editProduct.previewActionsHint')}
          </Text>

          <Group gap="xs" justify="flex-end" wrap="wrap">
            <EditorChipButton
              type="button"
              accentColor={color}
              leftIcon={<IconCamera size={15} stroke={1.75} />}
              onClick={() => void captureFromViewer()}
              disabled={!modelUrl}
            >
              {t('editProduct.currentModelView')}
            </EditorChipButton>

            <Text size="xs" c="dimmed" fw={500}>
              {t('editProduct.or')}
            </Text>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handlePreviewImageUpload(e.target.files?.[0] || null)}
              style={{ display: 'none' }}
            />

            <EditorChipButton
              accentColor={color}
              leftIcon={<IconPhotoUp size={15} stroke={1.75} />}
              onClick={() => fileInputRef.current?.click()}
            >
              {t('editProduct.uploadImage')}
            </EditorChipButton>
          </Group>
        </Stack>
      </Group>
    </Box>
  );
}
